import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import styled, { css } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';

import { getFontFamily } from 'services/FontService';
import { T } from 'services/ThemeTokens';
import { IColors } from 'types';
import {
  HEADER_COMPACT_MEDIA,
  HEADER_MID_MEDIA,
  MEDIA_QUERIES,
  mq,
} from 'components/constants';
import { useIsTouch } from 'core/hooks/Layout';
import { Icon, IconsNames } from 'atoms';

export interface IMenuItem extends IMenuItemStyle {
  children: string;
  to: string;
  /**
   * Значок пункта (lucide). Рисуется у пунктов выпадающего списка и у
   * пункта-одиночки в шапке; у заголовка группы его нет — шесть групп со
   * значками не помещаются в шапку на 1280 px.
   */
  icon?: LucideIcon;
  /**
   * Счётчики пункта (ТЗ 0096) — справа от подписи: у пункта выпадающего
   * списка и у пункта-одиночки в шапке. Пустой — ничего не рисуется.
   */
  badge?: React.ReactNode;
  /**
   * Точка у заголовка СВЁРНУТОЙ группы: в одном из пунктов есть счётчик, а
   * список закрыт. Строка — подпись для диктора и `title`; `null` — точки нет.
   */
  headDot?: string | null;
}

export interface IMenuItemStyle {
  colors?: IColors;
  isActive?: boolean;
  disabled?: boolean;
  isMulti?: IMenuItem[];
}

/**
 * ТЗ 0121: группа шапки раскрылась (мышью или с клавиатуры) — остальные
 * сворачиваются: два списка сразу не открыты никогда. `detail` — корень
 * группы, которая раскрылась.
 */
const MENU_GROUP_ENTER = 'erp:menu-group-enter';

const announceGroup = (root: HTMLLIElement | null) => {
  window.dispatchEvent(new CustomEvent(MENU_GROUP_ENTER, { detail: root }));
};

export const MenuItem = ({
  to,
  children,
  icon: ItemIcon,
  colors = {
    defaultColor: 'var(--k-fg-263446)',
    activeColor: 'var(--k-fg-88959e)',
  },
  isActive,
  disabled,
  isMulti,
  badge,
  headDot,
}: IMenuItem) => {
  /*
   * ТЗ 0121: выпадающий список открывается и по нажатию. Наведение — только
   * там, где оно есть (`(hover: hover)` в стилях); на сенсорном экране
   * (`(hover: none)`) первое нажатие на группу раскрывает список, второе —
   * сворачивает, нажатие мимо и переход — закрывают. С клавиатуры: ↓ / Пробел
   * раскрывают, Esc — сворачивает. На компьютере с мышью вид прежний.
   */
  const isTouch = useIsTouch();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Мышь зашла в другую группу или та раскрылась с клавиатуры — эта
  // (раскрытая нажатием / клавиатурой) сворачивается.
  useEffect(() => {
    if (!open) return undefined;
    const onOther = (event: Event) => {
      if ((event as CustomEvent).detail !== rootRef.current) setOpen(false);
    };
    window.addEventListener(MENU_GROUP_ENTER, onOther);
    return () => window.removeEventListener(MENU_GROUP_ENTER, onOther);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  const onHeadClick = (event: React.MouseEvent) => {
    if (!isMulti || !isTouch) return;
    event.preventDefault();
    if (!open) announceGroup(rootRef.current);
    setOpen((value) => !value);
  };

  const onHeadKeyDown = (event: React.KeyboardEvent) => {
    if (!isMulti) return;
    if (event.key === 'ArrowDown' || event.key === ' ') {
      event.preventDefault();
      announceGroup(rootRef.current);
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const onListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') setOpen(false);
  };

  // Фокус ушёл из группы (Tab дальше, щелчок мимо) — список сворачивается.
  const onBlur = (event: React.FocusEvent) => {
    if (!open) return;
    const next = event.relatedTarget as Node | null;
    if (!next || !rootRef.current?.contains(next)) setOpen(false);
  };

  // Наведение мышью на группу сворачивает раскрытые с клавиатуры соседние.
  const onMouseEnter = () => {
    if (isMulti && !isTouch) announceGroup(rootRef.current);
  };

  return (
    <StyledMenuItem
      ref={rootRef}
      isActive={isActive}
      colors={colors}
      disabled={disabled}
      isMulti={isMulti}
      $open={open}
      onBlur={onBlur}
      onMouseEnter={onMouseEnter}
    >
      <StyledLink
        to={to}
        onClick={onHeadClick}
        onKeyDown={onHeadKeyDown}
        aria-haspopup={isMulti ? 'true' : undefined}
        aria-expanded={isMulti ? open : undefined}
      >
        {ItemIcon ? (
          <ItemIcon size={15} strokeWidth={2} aria-hidden="true" />
        ) : null}
        <span>{children}</span>
        {!isMulti && badge ? <BadgeSlot>{badge}</BadgeSlot> : null}
        {isMulti && headDot ? (
          <HeadDot role="img" aria-label={headDot} title={headDot} />
        ) : null}
        {isMulti ? (
          <IconWrapper>
            <Icon
              name={IconsNames.ArrowIcon}
              width={10}
              height={6}
              color="var(--k-fg-0d53fc)"
            />
          </IconWrapper>
        ) : null}
      </StyledLink>
      {isMulti ? (
        <SubItemList onKeyDown={onListKeyDown}>
          {isMulti.map(
            ({
              children: text,
              to: itemTo,
              icon: SubIcon,
              badge: subBadge,
            }) => (
              <SubItem key={`${children}-${text}`}>
                {/* Нажатие на пункт сворачивает группу и тогда, когда адрес
                    тот же (переход на текущую страницу pathname не меняет). */}
                <StyledLink to={itemTo} onClick={() => setOpen(false)}>
                  {SubIcon ? (
                    <SubIcon size={15} strokeWidth={2} aria-hidden="true" />
                  ) : null}
                  <span>{text}</span>
                  {subBadge ? <BadgeSlot $push>{subBadge}</BadgeSlot> : null}
                </StyledLink>
              </SubItem>
            ),
          )}
        </SubItemList>
      ) : null}
    </StyledMenuItem>
  );
};

/**
 * Ссылка пункта. `nowrap`: подпись группы не переносится на вторую строку,
 * а список растёт по самому длинному пункту. Поля по бокам — 6 px, а не 8:
 * шесть групп «по задачам» (2026-09-22) должны влезть в шапку на 1280 px.
 */
const StyledLink = styled(Link)`
  box-sizing: border-box;
  padding: 8px 6px;
  font-family: ${getFontFamily('bold')};
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  letter-spacing: 0.5px;
  text-decoration: none;
  white-space: nowrap;

  ${HEADER_COMPACT_MEDIA} {
    padding: 8px 4px;
    font-size: 13px;
    letter-spacing: 0;
  }
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--k-fg-263446);

  & > svg {
    flex: none;
  }
`;

/**
 * Место счётчиков в строке пункта. В выпадающем списке (`$push`) прижато
 * вправо — числа разных пунктов стоят одной колонкой.
 */
const BadgeSlot = styled.span<{ $push?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex: none;
  margin-left: ${({ $push }) => ($push ? 'auto' : '0')};
  padding-left: ${({ $push }) => ($push ? '12px' : '0')};
  font-family: ${getFontFamily('bold')};
  letter-spacing: 0;
`;

/** Точка «есть новое» у заголовка свёрнутой группы. */
const HeadDot = styled.span`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--k-bg-0d53fc);
`;

/**
 * `width: max-content` — перепроверка U3: у абсолютного списка без ширины
 * браузер берёт ширину «по минимуму содержимого», а значки счётчиков в
 * строке пункта умеют переноситься — и в этот минимум не входят. С двумя
 * счётчиками («Коммуникатор» 238 + 12) второй чип вылезал за правый край
 * выпадашки. Теперь список растёт по самой длинной строке целиком.
 */
const SubItemList = styled.ul`
  position: absolute;
  top: calc(100% - 8px);
  left: 0;
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: 230px;
  border: 1px solid var(--k-border-ebeff2);
  box-shadow: 0px 4px 8px var(--k-shadow-88959e-a80);
  list-style: none;
  opacity: 0;
  visibility: hidden;
  transition: visibility 0s 0.15s, top 0s 0.15s, opacity 0s 0.15s,
    transform 0s 0.15s;

  /* ТЗ 0121: уже компьютера и на сенсорном экране закрытый список сжат в
     точку — невидимый, но широкий \`ul\` у правого края шапки раздвигал
     страницу на планшете (820 → 825). Компьютер с мышью — как было. */
  @media ${MEDIA_QUERIES.touch}, ${MEDIA_QUERIES.belowDesktop} {
    transform: scale(0);
    transform-origin: top left;
  }
`;

const SubItem = styled.li`
  height: 40px;
  background-color: var(--k-bg-ffffff);

  /* Двоеточия здесь не было («background-color #…»), и подсветка пункта под
     курсором молча не работала. */
  &:hover {
    background-color: var(--k-bg-f2f5f9);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--k-border-ebeff2);
  }

  & > ${StyledLink} {
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    padding: 0 16px;
    gap: 10px;
    font-family: ${getFontFamily('medium')};
    font-weight: normal;
    color: var(--k-fg-263446);
  }

  & > ${StyledLink} > svg {
    color: var(--k-fg-616e78);
  }

  &:hover > ${StyledLink} > svg {
    color: var(--k-fg-0d53fc);
  }
`;

/** Стрелка группы: 10 px без полей — отступ от подписи даёт `gap` ссылки. */
const IconWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  width: 10px;
  transform: rotate(-180deg);
  transition: transform 0.25s ease-in-out 0.15s;
`;

/** Раскрытая группа: рамка, список под пунктом, стрелка вверх. */
const openListCss = css`
  border-color: ${T.border};
  transition: border-color 0s 0s;

  ${SubItemList} {
    top: calc(100% + 8px);
    opacity: 1;
    visibility: visible;
    transform: none;
    transition: visibility 0s 0s, top 0.25s ease-in-out,
      opacity 0.3s ease-in-out, transform 0s 0s;
  }

  ${IconWrapper} {
    transform: rotate(0);
    transition: transform 0.25s ease-in-out;
  }
`;

const StyledMenuItem = styled.li<IMenuItemStyle & { $open?: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  z-index: 1;

  ${({ isMulti }) =>
    isMulti
      ? `border: 1px solid;
       border-color: transparent;
       transition: border-color 0s 0.15s;
    `
      : ``}

  & > ${StyledLink} {
    color: ${({ colors, isActive }) =>
      isActive ? colors?.activeColor : colors?.defaultColor};
    opacity: ${({ disabled }) => (disabled ? '0.1' : '1')};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  }

  /* 16, а не 32: шесть групп меню помещаются в шапку на 1280 px. */
  &:not(:last-child) {
    margin-right: 16px;

    ${HEADER_MID_MEDIA} {
      margin-right: 12px;
    }

    ${HEADER_COMPACT_MEDIA} {
      margin-right: 8px;
    }
  }

  /* Наведение — только у устройств с мышью: на сенсорном экране «залипший»
     :hover после нажатия не дал бы свернуть список вторым нажатием. */
  ${mq.hover} {
    &:hover {
      ${({ isMulti }) => (isMulti ? openListCss : '')}
    }
  }

  /* Раскрыт нажатием или с клавиатуры (ТЗ 0121). */
  ${({ isMulti, $open }) => (isMulti && $open ? openListCss : '')}
`;
